import React, { useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { db, auth } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, increment } from "firebase/firestore";

import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import "./AIItinerary.css";

const interestsList = [
  "Food",
  "Beaches",
  "Mountains",
  "Culture",
  "Nightlife",
  "Wildlife",
  "Shopping",
];
const activitiesList = [
  "Scuba Diving",
  "Hiking",
  "Spa",
  "Art Galleries",
  "Desert Safari",
  "Wildlife",
];

const PLAN_LIMITS = {
  free: 2,
  pro: 10,
  elite: 25,
  luxe: 100,
};
// Top of AIItinerary.jsx
const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

/**
 * Send an email via Formspree. Expects a FLAT object of fields.
 */
async function sendFormspreeEmail(fields) {
  if (!FORMSPREE_ENDPOINT) {
    throw new Error(
      "REACT_APP_FORMSPREE_ENDPOINT is not set. Add it to your .env and restart the dev server."
    );
  }

  // Use FormData (do NOT set Content-Type manually).
  const fd = new FormData();
  // Subject that appears in the notification
  fd.append(
    "subject",
    fields.subject || "TravXplo — new travel-expert request"
  );
  // Add all other fields
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
  });

  const res = await fetch(FORMSPREE_ENDPOINT, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: fd,
  });

  // Helps debugging if something goes wrong
  let data = {};
  try {
    data = await res.json();
  } catch {}

  if (!res.ok || data?.error) {
    throw new Error(data?.error || `Formspree error: ${res.status}`);
  }
}

/* ---------- Travelers picker (Adults/Children) ---------- */
function TravelersPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const inc = (k) =>
    onChange({ ...value, [k]: Math.min(9, (value[k] ?? 0) + 1) });
  const dec = (k) =>
    onChange({
      ...value,
      [k]: Math.max(k === "adults" ? 1 : 0, (value[k] ?? 0) - 1),
    });

  const label =
    `${value.adults} Adult${value.adults > 1 ? "s" : ""}` +
    (value.children
      ? `, ${value.children} Child${value.children > 1 ? "ren" : ""}`
      : "");

  return (
    <div className="picker" ref={ref}>
      <div
        className="picker-toggle"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setOpen(!open)
        }
      >
        {label}
      </div>

      {open && (
        <div className="picker-menu">
          <div className="picker-row">
            <div className="picker-row-label">Adults</div>
            <div className="counter">
              <button
                onClick={() => dec("adults")}
                disabled={value.adults <= 1}
              >
                –
              </button>
              <div className="count">{value.adults}</div>
              <button onClick={() => inc("adults")}>+</button>
            </div>
          </div>

          <div className="picker-row">
            <div className="picker-row-label">Children</div>
            <div className="counter">
              <button
                onClick={() => dec("children")}
                disabled={value.children <= 0}
              >
                –
              </button>
              <div className="count">{value.children}</div>
              <button onClick={() => inc("children")}>+</button>
            </div>
          </div>

          <div className="picker-actions">
            <button className="btn small" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const AIItinerary = () => {
  const [form, setForm] = useState({
    departure: "",
    destination: "",
    startDate: "",
    days: 1,
    // tripType removed
    budget: "",
    hotel: "",
    hotelTier: "", // NEW: hotel price range (per night)
    travelStyle: "",
    interests: [],
    activities: [],
    companions: "",
    pace: "",
    travelers: { adults: 1, children: 0 }, // NEW: travelers
  });

  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState({ used: 0, limit: 2 });
  const [usageLoading, setUsageLoading] = useState(true);
  const [showJson, setShowJson] = useState(false);
  const [contactSending, setContactSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // 🔹 Helper: Ensure user doc exists
  const ensureUserDoc = async (user) => {
    if (!user) return null;
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      await setDoc(userRef, {
        itinerariesUsed: 0,
        itineraryLimit: 2,
        planType: "free",
        email: user.email,
        createdAt: new Date(),
      });
      return { itinerariesUsed: 0, itineraryLimit: 2 };
    }
    return snap.data();
  };

  useEffect(() => {
    const fetchUsage = async () => {
      setUsageLoading(true);
      const user = auth.currentUser;

      if (!user) {
        setUsage({ used: 0, limit: 2 });
        setUsageLoading(false);
        return;
      }

      const data = await ensureUserDoc(user);
      setUsage({
        used: Math.min(data.itinerariesUsed ?? 0, data.itineraryLimit ?? 2),
        limit: data.itineraryLimit ?? 2,
      });
      setUsageLoading(false);
    };

    fetchUsage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleCheckboxChange = (name, value) => {
    setForm((prev) => {
      const updated = prev[name].includes(value)
        ? prev[name].filter((v) => v !== value)
        : [...prev[name], value];
      return { ...prev, [name]: updated };
    });
  };
  // top of file (or above the component)
  const withTimeout = (promise, ms, label) =>
    Promise.race([
      promise,
      new Promise((_, rej) =>
        setTimeout(() => rej(new Error(`${label} timeout after ${ms}ms`)), ms)
      ),
    ]);

  // inside AIItinerary component:

  // helper to remove all undefined values (Firestore rejects them)
  const stripUndefined = (obj) => JSON.parse(JSON.stringify(obj));

  const handleContactExpert = async (selectedHotel = null) => {
    if (!itinerary) {
      alert("Please generate an itinerary first.");
      return;
    }
    setContactSending(true);
    try {
      const user = auth.currentUser;

      const tierLabel =
        form.hotelTier === "luxury_5"
          ? "Luxury (5★)"
          : form.hotelTier === "premium_4"
          ? "Premium (4★)"
          : form.hotelTier === "mid_3"
          ? "Mid-range (3★)"
          : "";

      const requestPayload = {
        createdAt: serverTimestamp(),
        userUid: user?.uid || null,
        userEmail: user?.email || null,

        form: {
          departure: form.departure || null,
          destination: form.destination || null,
          startDate: form.startDate || null,
          days: Number(form.days) || 1,
          hotelPriceTier: tierLabel || null,
          travelStyle: form.travelStyle || null,
          interests: form.interests || [],
          activities: form.activities || [],
          companions: form.companions || null,
          pace: form.pace || null,
          adults: form.travelers?.adults ?? 1,
          children: form.travelers?.children ?? 0,
        },

        overview: itinerary.overview || null,
        flights: itinerary.flights || [],
        hotels: itinerary.hotels || [],
        chosenHotel: selectedHotel || null,
      };

      // 1) Save to Firestore
      const docRef = await addDoc(
        collection(db, "expert_requests"),
        JSON.parse(JSON.stringify(requestPayload)) // strip undefined
      );

      // 2) Email via Formspree (flat fields)
      await sendFormspreeEmail({
        subject: `New itinerary request: ${form.destination} (${form.startDate})`,
        userEmail: user?.email || "",
        docId: docRef.id,
        destination: form.destination,
        startDate: form.startDate,
        days: Number(form.days) || 1,
        adults: form.travelers?.adults ?? 1,
        children: form.travelers?.children ?? 0,
        hotelTier: tierLabel,
        travelStyle: form.travelStyle || "",
        interests: form.interests.join(", "),
        activities: form.activities.join(", "),
        companions: form.companions || "",
        message: "A new expert request has been stored in Firestore.",
      });

      setShowSuccess(true);
    } catch (err) {
      console.error(err);
      alert("Couldn’t send the request. Please try again.");
    } finally {
      setContactSending(false);
    }
  };

  const generateItinerary = async () => {
    const user = auth.currentUser;

    if (!user) {
      alert("Please log in to generate itineraries.");
      return;
    }
    // ensure doc exists
    const data = await ensureUserDoc(user);

    // strict check BEFORE increment
    if (data.itinerariesUsed >= data.itineraryLimit) {
      alert("You’ve used your free itineraries. Please upgrade.");
      window.location.href = "/PricingPlans";

      return;
    }

    // validate form
    if (!form.departure || !form.destination || !form.startDate || !form.days) {
      alert("Please fill Departure, Destination, Start Date, and Days.");
      return;
    }

    try {
      setLoading(true);
      // increment usage
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        itinerariesUsed: increment(1),
      });

      const newSnap = await getDoc(userRef);
      setUsage({
        used: Math.min(
          newSnap.data().itinerariesUsed,
          newSnap.data().itineraryLimit
        ),
        limit: newSnap.data().itineraryLimit,
      });
      const tierLabel =
        form.hotelTier === "luxury_5"
          ? "Luxury (5★)"
          : form.hotelTier === "premium_4"
          ? "Premium (4★)"
          : form.hotelTier === "mid_3"
          ? "Mid-range (3★)"
          : "";

      const payload = {
        user_id: user.uid,
        departure_city: form.departure,
        destination_city: form.destination,
        start_date: form.startDate,
        number_of_days: Number(form.days),

        adults: Number(form.travelers?.adults ?? 1),
        children: Number(form.travelers?.children ?? 0),
        hotel_price_tier: tierLabel,

        budget_range: form.budget,
        travel_style: form.travelStyle,
        interests: form.interests,
        preferred_activities: form.activities,
        preferred_travel_pace: form.pace,
        travel_companions: form.companions,
      };

      const res = await fetch(
        `${
          process.env.REACT_APP_API_BASE || "http://localhost:8000"
        }/api/itinerary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const obj = await res.json();
      setItinerary(obj);
      console.log("Backend Response:", obj);
    } catch (e) {
      console.error(e);
      setItinerary({ error: "Error generating itinerary." });
    } finally {
      setLoading(false);
    }
  };
  // const saveItinerary = async () => {
  //   const user = auth.currentUser;
  //   if (!user) {
  //     alert("Please log in to save your itinerary.");
  //     return;
  //   }
  //   if (!itinerary) {
  //     alert("No itinerary generated yet.");
  //     return;
  //   }

  //   try {
  //     await addDoc(collection(db, "users", user.uid, "saved_itineraries"), {
  //       itinerary:
  //         typeof itinerary === "string"
  //           ? itinerary
  //           : JSON.parse(JSON.stringify(itinerary)),
  //       createdAt: serverTimestamp(),
  //     });
  //     alert("✅ Itinerary saved to your dashboard!");
  //   } catch (err) {
  //     console.error("Save failed:", err);
  //     alert("❌ Could not save itinerary. Try again.");
  //   }
  // };
  const saveItinerary = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to save your itinerary.");
      return;
    }
    if (!itinerary) {
      alert("No itinerary generated yet.");
      return;
    }

    try {
      await addDoc(collection(db, "users", user.uid, "saved_itineraries"), {
        itinerary:
          typeof itinerary === "string"
            ? { text: itinerary } // if plain text, wrap in object
            : JSON.parse(JSON.stringify(itinerary)), // if JSON, keep full structure
        createdAt: serverTimestamp(),
      });
      alert("✅ Full itinerary saved to your dashboard!");
    } catch (err) {
      console.error("Save failed:", err);
      alert("❌ Could not save itinerary. Try again.");
    }
  };

  const downloadPDF = () => {
    const el = document.getElementById("itinerary-output");
    if (!el) return;

    const opt = {
      margin: 0.5, // inches
      filename: "itinerary.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
      },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .set(opt)
      .from(el)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const total = pdf.internal.getNumberOfPages();
        const { width, height } = pdf.internal.pageSize;

        // ---- watermark config ----
        const TEXT = "TRAVXPLO";
        const CORNER = "bottom-right"; // <-- change to "top-right" if you prefer
        const MARGIN = 0.3; // inches from the page edges
        const FONT_SIZE = 10; // small and discreet
        const OPACITY = 0.12; // very light

        // Try to use a low opacity; if GState not available, light grey text still looks subtle
        const setOpacity = (val) => {
          try {
            const g = new pdf.GState({ opacity: val });
            pdf.setGState(g);
          } catch {
            /* noop for older builds */
          }
        };

        setOpacity(OPACITY);
        pdf.setTextColor(120, 120, 120); // fallback light grey
        pdf.setFontSize(FONT_SIZE);

        for (let i = 1; i <= total; i++) {
          pdf.setPage(i);

          const x = width - MARGIN; // right edge
          const y =
            CORNER === "top-right"
              ? MARGIN + FONT_SIZE * 0.035 /* small vertical tweak */
              : height - MARGIN;

          // Right-aligned so the text hugs the edge neatly
          pdf.text(TEXT, x, y, { align: "right" });
        }

        setOpacity(1); // reset for anything else that might be drawn
      })
      .save();
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="ai-itinerary-container">
      <h1>Itinerary Planner</h1>
      {/* ✅ Safe check: only render if planType exists */}
      {/* {itinerary.planType && (
        <p style={{ fontStyle: "italic", color: "#555" }}>
          Generated using your <strong>{itinerary.planType}</strong> plan
        </p>
      )} */}

      {/* FORM */}
      <div className="form-wrapper">
        <div className="form-grid">
          {/* Left Column */}
          <div className="form-column">
            <label>
              Departure City
              <input
                name="departure"
                type="text"
                value={form.departure}
                onChange={handleChange}
                placeholder="Flying from"
              />
            </label>

            <label>
              Start Date of the Trip
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
              />
            </label>

            {/* NEW: Hotel price range (per night) */}
            <label>
              Hotel Price Range (per night)
              <select
                name="hotelTier"
                value={form.hotelTier}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="luxury_5">Luxury (5★)</option>
                <option value="premium_4">Premium (4★)</option>
                <option value="mid_3">Mid-range (3★)</option>
              </select>
            </label>

            <div className="checkbox-group">
              <p>Interests</p>
              {interestsList.map((interest) => (
                <label key={interest}>
                  <input
                    type="checkbox"
                    checked={form.interests.includes(interest)}
                    onChange={() => handleCheckboxChange("interests", interest)}
                  />
                  {interest}
                </label>
              ))}
            </div>

            <label>
              Travel Companions
              <select
                name="companions"
                value={form.companions}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option>Solo</option>
                <option>Couple</option>
                <option>Friends</option>
                <option>Family</option>
                <option>Workation</option>
              </select>
            </label>
          </div>

          {/* Right Column */}
          <div className="form-column">
            <label>
              Destination City
              <input
                name="destination"
                value={form.destination}
                onChange={handleChange}
                placeholder="Destination"
                type="text"
              />
            </label>

            <label>
              Number of Days
              <input
                type="number"
                name="days"
                min="1"
                value={form.days}
                onChange={handleChange}
              />
            </label>

            {/* NEW: Travellers */}
            <label>
              Travellers
              <TravelersPicker
                value={form.travelers}
                onChange={(v) => setForm((p) => ({ ...p, travelers: v }))}
              />
            </label>

            <label>
              Travel Style
              <select
                name="travelStyle"
                value={form.travelStyle}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option>Planned to the minute</option>
                <option>Balanced</option>
                <option>Leisurely</option>
              </select>
            </label>

            <div className="checkbox-group">
              <p>Preferred Activities</p>
              {activitiesList.map((act) => (
                <label key={act}>
                  <input
                    type="checkbox"
                    checked={form.activities.includes(act)}
                    onChange={() => handleCheckboxChange("activities", act)}
                  />
                  {act}
                </label>
              ))}
            </div>
          </div>
        </div>
        {/* Usage tracker */}
        <div
          style={{
            background: "#f0f4ff",
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "8px",
            border: "1px solid #cbd5e1",
            display: "flex",
            justifyContent: "space-between",
            marginTop: "12px",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "15px", color: "#1e293b" }}>
            {usageLoading ? (
              <>Loading your quota...</>
            ) : usage.used < usage.limit ? (
              <>
                You have <strong>{usage.limit - usage.used}</strong> itinerary
                {usage.limit - usage.used !== 1 ? "ies" : ""} left from your
                free plan.
              </>
            ) : (
              <>
                Your free plan is over. Please upgrade to continue using the
                planner.
              </>
            )}
          </span>

          {/* 🔹 Show Upgrade button ONLY when usage.used >= usage.limit */}
          {!usageLoading && usage.used >= usage.limit && (
            <button
              onClick={() => (window.location.href = "/PricingPlans")}
              style={{
                backgroundColor: "#2563eb",

                color: "white",
                padding: "8px 14px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Upgrade Now
            </button>
          )}
        </div>
        <div className="form-actions">
          <button
            className="btn"
            onClick={generateItinerary}
            disabled={loading || usageLoading || usage.used >= usage.limit}
          >
            {loading ? "Generating..." : "Generate Itinerary"}
          </button>
        </div>
        {/* LOADING MESSAGE */}
        {loading && (
          <div className="loading-message">
            ⏳ Please wait… your itinerary is being generated.
          </div>
        )}
      </div>

      {/* RESULT (unchanged below) */}
      {itinerary && (
        <div className="output-section">
          <div id="itinerary-output" className="itinerary-pretty">
            <div className="watermark">TravXplo</div>
            {/* HERO */}
            <div className="hero">
              {itinerary.cover?.image_url ? (
                <img
                  className="hero-img"
                  src={itinerary.cover.image_url}
                  alt={itinerary.overview?.destination || "Destination"}
                  crossOrigin="anonymous" // 👈 important
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="hero-placeholder">No Image</div>
              )}
              <div className="hero-overlay">
                <h2>{itinerary.overview?.title || "Your Trip"}</h2>
                <div className="hero-meta">
                  <span className="chip">
                    {itinerary.overview?.destination?.toUpperCase()}
                  </span>
                  <span className="chip">
                    {formatDate(itinerary.overview?.start_date)} •{" "}
                    {itinerary.overview?.days} day
                    {Number(itinerary.overview?.days) > 1 ? "s" : ""}
                  </span>
                  {itinerary.overview?.style && (
                    <span className="chip">{itinerary.overview.style}</span>
                  )}
                  {itinerary.overview?.companions && (
                    <span className="chip">
                      {itinerary.overview.companions}
                    </span>
                  )}
                </div>
                {/* {itinerary.cover?.credit && (
                  <div className="hero-credit">📸 {itinerary.cover.credit}</div>
                )} */}
              </div>
            </div>

            {/* WEATHER OVERVIEW */}
            {itinerary.weather_overview && !itinerary.weather_multi && (
              <section className="section">
                <h3>Weather Overview</h3>
                <div className="card">
                  <div className="card-body">
                    <p style={{ marginTop: 0 }}>
                      {itinerary.weather_overview.summary}
                    </p>
                    <div className="kv">
                      <span>Avg High:</span>
                      <span>
                        {itinerary.weather_overview.avg_high_c ?? "—"}°C
                      </span>
                    </div>
                    <div className="kv">
                      <span>Avg Low:</span>
                      <span>
                        {itinerary.weather_overview.avg_low_c ?? "—"}°C
                      </span>
                    </div>
                    {typeof itinerary.weather_overview.rain_chance_avg ===
                      "number" && (
                      <div className="kv">
                        <span>Avg Rain Chance:</span>
                        <span>
                          {itinerary.weather_overview.rain_chance_avg}%
                        </span>
                      </div>
                    )}
                    {itinerary.weather_overview.best_time_to_visit && (
                      <div className="kv">
                        <span>Best Time to Visit:</span>
                        <span>
                          {itinerary.weather_overview.best_time_to_visit}
                        </span>
                      </div>
                    )}
                    {Array.isArray(itinerary.weather_overview.tips) &&
                      itinerary.weather_overview.tips.length > 0 && (
                        <>
                          <div className="list-title" style={{ marginTop: 12 }}>
                            Tips
                          </div>
                          <ul className="bullets">
                            {itinerary.weather_overview.tips.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    <div className="muted" style={{ marginTop: 8 }}>
                      Source:{" "}
                      {itinerary.weather_overview.source === "forecast"
                        ? "Real-time forecast"
                        : "Climate normals"}
                    </div>
                  </div>
                </div>
              </section>
            )}
            {/* MULTI-CITY WEATHER */}
            {Array.isArray(itinerary.weather_multi) &&
              itinerary.weather_multi.length > 0 && (
                <section className="section">
                  <h3>Weather Overview (per city)</h3>
                  <div className="card-grid">
                    {itinerary.weather_multi.map((w, idx) => (
                      <div className="card" key={idx}>
                        <div className="card-title">🌤 {w.city}</div>
                        <div className="card-body">
                          <p>{w.summary}</p>
                          <div className="kv">
                            <span>Avg High:</span>
                            <span>{w.avg_high_c ?? "—"}°C</span>
                          </div>
                          <div className="kv">
                            <span>Avg Low:</span>
                            <span>{w.avg_low_c ?? "—"}°C</span>
                          </div>
                          {w.best_time_to_visit && (
                            <div className="kv">
                              <span>Best Time:</span>
                              <span>{w.best_time_to_visit}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* GROUND TRANSFER (for non-airport cities) */}
            {itinerary.ground_transfer && (
              <section className="section">
                <h3>Ground Transfer to Nearest Airport</h3>
                <div className="card">
                  <div className="card-title">
                    🚗 {itinerary.ground_transfer.from} →{" "}
                    {itinerary.ground_transfer.to}
                  </div>
                  <div className="card-body">
                    <div className="kv">
                      <span>Mode:</span>
                      <span>{itinerary.ground_transfer.mode}</span>
                    </div>
                    <div className="kv">
                      <span>Distance:</span>
                      <span>{itinerary.ground_transfer.distance_km} km</span>
                    </div>
                    <div className="kv">
                      <span>Duration:</span>
                      <span>~{itinerary.ground_transfer.duration_hr} hrs</span>
                    </div>
                    {itinerary.ground_transfer.notes && (
                      <div className="muted" style={{ marginTop: 8 }}>
                        {itinerary.ground_transfer.notes}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* FLIGHTS */}
            {Array.isArray(itinerary.flights) &&
              itinerary.flights.length > 0 && (
                <section className="section">
                  <h3>Flight Options</h3>
                  <div className="card-grid">
                    {itinerary.flights.map((f, idx) => (
                      <div className="card" key={idx}>
                        <div className="card-title">
                          ✈️ {f.from} → {f.to}{" "}
                          {f.carrier ? `• ${f.carrier}` : ""}
                        </div>
                        <div className="card-body">
                          <div className="kv">
                            <span>Depart:</span>
                            <span>{f.depart}</span>
                          </div>
                          {f.arrive && (
                            <div className="kv">
                              <span>Arrive:</span>
                              <span>{f.arrive}</span>
                            </div>
                          )}
                          {f.price && (
                            <div className="kv">
                              <span>Price:</span>
                              <span>₹{Number(f.price).toLocaleString()}</span>
                            </div>
                          )}
                          {f.booking_link && (
                            <a
                              className="btn link"
                              href={f.booking_link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Book / See on Google Flights
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* HOTELS */}
            {Array.isArray(itinerary.hotels) &&
              itinerary.hotels.length > 0 &&
              !itinerary.hotels_multi && (
                <section className="section">
                  <h3>Stay Suggestions</h3>
                  <div className="card-grid">
                    {itinerary.hotels.map((h, idx) => (
                      <div className="card" key={idx}>
                        <div className="card-title">🏨 {h.name}</div>
                        <div className="card-body">
                          {h.address && (
                            <div className="muted">{h.address}</div>
                          )}
                          <div className="kv">
                            <span>Check-in:</span>
                            <span>{formatDate(h.checkin)}</span>
                          </div>
                          <div className="kv">
                            <span>Check-out:</span>
                            <span>{formatDate(h.checkout)}</span>
                          </div>
                          {h.price && (
                            <div className="kv">
                              <span>Price:</span>
                              <span>₹{Number(h.price).toLocaleString()}</span>
                            </div>
                          )}
                          {h.booking_link && (
                            <a
                              className="btn link"
                              href={h.booking_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={
                                h.official_site
                                  ? "Go to official site"
                                  : "View on Google Hotels"
                              }
                            >
                              {h.official_site
                                ? "Official site"
                                : "View / Book"}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="expert-cta">
                    <p>
                      Want a better price? Our travel expert can often beat
                      online rates.
                    </p>
                    <button
                      className="btn expert"
                      onClick={() => handleContactExpert()}
                      disabled={contactSending}
                    >
                      {contactSending
                        ? "Sending..."
                        : "Contact our travel expert"}
                    </button>
                  </div>
                </section>
              )}

            {/* MULTI-CITY HOTELS */}
            {itinerary.hotels_multi &&
              Object.keys(itinerary.hotels_multi).length > 0 && (
                <section className="section">
                  <h3>Stay Suggestions (per city)</h3>
                  {Object.entries(itinerary.hotels_multi).map(
                    ([city, hotels]) => (
                      <div key={city} style={{ marginBottom: "20px" }}>
                        <h4 style={{ marginBottom: "10px" }}>🏙 {city}</h4>
                        <div className="card-grid">
                          {hotels.map((h, idx) => (
                            <div className="card" key={idx}>
                              <div className="card-title">🏨 {h.name}</div>
                              <div className="card-body">
                                {h.address && (
                                  <div className="muted">{h.address}</div>
                                )}
                                {h.price && (
                                  <div className="kv">
                                    <span>Price:</span>
                                    <span>
                                      ₹{Number(h.price).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                {h.booking_link && (
                                  <a
                                    className="btn link"
                                    href={h.booking_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View / Book
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </section>
              )}
            {/* PLAN */}
            {Array.isArray(itinerary.plan) && itinerary.plan.length > 0 && (
              <section className="section">
                <h3>Day-by-Day Plan</h3>
                <div className="days">
                  {itinerary.plan.map((d, idx) => (
                    <div className="day" key={idx}>
                      <div className="day-header">
                        <div className="day-title">
                          Day {idx + 1} • {formatDate(d.date)}
                        </div>
                        {Array.isArray(d.transfers) &&
                          d.transfers.length > 0 && (
                            <div className="transfers">
                              <div className="transfers-title">
                                Getting around
                              </div>
                              {d.transfers.map((leg, i2) => (
                                <div className="transfer-row" key={i2}>
                                  <div className="transfer-path">
                                    <span className="from">{leg.from}</span>
                                    <span className="arrow">→</span>
                                    <span className="to">{leg.to}</span>
                                  </div>
                                  <div className="transfer-meta">
                                    <span
                                      className={`mode pill pill-${(
                                        leg.mode || "walk"
                                      ).toLowerCase()}`}
                                    >
                                      {leg.mode || "Walk"}
                                    </span>
                                    {leg.minutes != null && (
                                      <span className="mins">
                                        ~{leg.minutes} min
                                      </span>
                                    )}
                                    {leg.map_link && (
                                      <a
                                        className="map-link"
                                        href={leg.map_link}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Open in Maps
                                      </a>
                                    )}
                                  </div>
                                  {leg.notes && (
                                    <div className="transfer-notes">
                                      {leg.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>

                      <div className="day-block">
                        <div className="slot">
                          <div className="slot-title">Morning</div>
                          <div className="slot-text">{d.morning}</div>
                        </div>
                        <div className="slot">
                          <div className="slot-title">Afternoon</div>
                          <div className="slot-text">{d.afternoon}</div>
                        </div>
                        <div className="slot">
                          <div className="slot-title">Evening</div>
                          <div className="slot-text">{d.evening}</div>
                        </div>
                      </div>

                      {Array.isArray(d.attractions) &&
                        d.attractions.length > 0 && (
                          <div className="list-wrap">
                            <div className="list-title">Attractions</div>
                            <ul className="link-list">
                              {d.attractions.map((a, i2) => (
                                <li key={i2}>
                                  <a
                                    href={a.official_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {a.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                      {d.restaurants?.length > 0 && (
                        <section style={{ marginTop: 16 }}>
                          <h4>Food Spots</h4>
                          <ul className="food-list">
                            {d.restaurants.map((r, i) => (
                              <li key={i}>
                                <a
                                  href={r.link}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {r.name}
                                </a>
                                {r.type && (
                                  <span
                                    className="chip"
                                    style={{ marginLeft: 8 }}
                                  >
                                    {r.type.replace("_", " ")}
                                  </span>
                                )}
                                {r.famous_for && (
                                  <span className="muted">
                                    {" "}
                                    — {r.famous_for}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* BUDGET */}
            {Array.isArray(itinerary.budget_breakdown) &&
              itinerary.budget_breakdown.length > 0 && (
                <section className="section">
                  <h3>Budget Breakdown</h3>
                  <div className="budget">
                    {itinerary.budget_breakdown.map((b, i) => (
                      <div className="budget-row" key={i}>
                        <span>{b.label}</span>
                        <span>{b.amount}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* TIPS */}
            {Array.isArray(itinerary.tips) && itinerary.tips.length > 0 && (
              <section className="section">
                <h3>Practical Tips</h3>
                <ul className="bullets">
                  {itinerary.tips.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* WARNINGS */}
            {Array.isArray(itinerary.warnings) &&
              itinerary.warnings.length > 0 &&
              (() => {
                console.warn("⚠️ Itinerary Notes:", itinerary.warnings);
                return null; // nothing renders in UI
              })()}
          </div>

          <div className="actions">
            {itinerary && (
              <div className="output-section">
                <div id="itinerary-output" className="itinerary-pretty">
                  {/* existing itinerary display */}
                </div>

                <button
                  className="download-btn save-btn"
                  onClick={saveItinerary}
                >
                  Save to Dashboard
                </button>
              </div>
            )}

            <button className="download-btn" onClick={downloadPDF}>
              Download PDF
            </button>
          </div>

          {showJson && (
            <div className="itinerary-box">
              <pre>{JSON.stringify(itinerary, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {showSuccess && (
        <div className="modal-backdrop" onClick={() => setShowSuccess(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h4>Request sent ✅</h4>
            <p>
              Your message was sent successfully to our travel expert. We’ll
              contact you soon.
            </p>
            <button className="btn" onClick={() => setShowSuccess(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIItinerary;
